import React, { useRef, useState } from "react";
import { useTrust } from "../../lib/context/TrustContext";
import { useReactToPrint } from "react-to-print";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { Button } from "../../components/ui/button";
import moment from "moment";
import HtmlContent from "../../components/HTMLContent";
import {
  formatNumber,
  formatSubcontractorName,
  getModuleNamingBySlug,
} from "../../lib/Misc";
import { useOrganization } from "../../lib/context/OrganisationContext";

const PaymentAdviceTemplate = ({
  showView,
  setShowView,
  popupType = "",
  value,
  paymentData,
}) => {
  const printRef = useRef(null);
  const { organizationData } = useOrganization();
  const { trustData } = useTrust();
  const defaultText = `We would like to confirm that we have received the funds in the trust account on your behalf in line with your prior instructions. As instructed, we have deducted the professional fees, paid to suppliers and have processed the remaining balance into the nominated bank account.`;

  // eslint-disable-next-line no-unused-vars
  const [templateData, setTemplateData] = useState(
    paymentData
      ? { ...paymentData, vendor: paymentData?.payment_detail?.[0]?.vendor }
      : {
          transaction_date: moment(new Date())?.format("DD-MM-YYYY"),
          invoice_number: "001",
          vendor: {
            last_name: `{{ ${getModuleNamingBySlug(
              organizationData?.organizationData,
              "vendors",
              "Vendor"
            )} Name }}`,
            street: `{{ ${getModuleNamingBySlug(
              organizationData?.organizationData,
              "vendors",
              "Vendor"
            )} Address }}`,
          },
        }
  );

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    contentRef: printRef,
    documentTitle: "Trust Receipt",
    removeAfterPrint: false, // Optional: prevents issues with disappearing modals
  });

  const htmlData = () => {
    return (
      <div className="p-6 text-sm font-sans">
        {/* Header */}
        <div className="mb-6">
          {trustData?.profile_pic && (
            <img
              src={trustData?.profile_pic}
              alt="Trust Logo"
              className="h-14 mb-2"
            />
          )}
          {trustData?.email && (
            <p className="font-semibold">{trustData?.email}</p>
          )}
          {trustData?.phone && <p>{trustData?.phone}</p>}
          {trustData?.street && <p>{trustData?.street},</p>}
          {trustData?.suburb?.name && (
            <p>
              {trustData?.suburb?.name}
              {`${
                trustData?.state?.short_name
                  ? `, ${trustData?.state?.short_name}`
                  : ""
              }`}
              {`${trustData?.postal_code ? `, ${trustData?.postal_code}` : ""}`}
            </p>
          )}
        </div>

        {/* CRN and Date */}
        <div className="mb-4">
          {templateData?.transaction_date && (
            <p>
              {moment(templateData?.transaction_date, "DD-MM-YYYY")?.format(
                "DD MMM, YYYY"
              )}
            </p>
          )}
          {templateData?.invoice_number && (
            <p className="italic">
              Receipt Number: {templateData?.invoice_number}
            </p>
          )}
        </div>

        {/* Recipient */}
        <p>
          {/* {templateData?.vendor &&
            (templateData?.vendor?.first_name
              ? templateData?.vendor?.first_name +
                " " +
                templateData?.vendor?.last_name
              : templateData?.vendor?.last_name)} */}

          {formatSubcontractorName(templateData?.vendor)}
        </p>
        {templateData?.vendor?.international_address ? (
          <p>
            {templateData?.vendor?.full_address && (
              <p>{templateData?.vendor?.full_address},</p>
            )}
            {templateData?.vendor?.country?.name && (
              <p>{templateData?.vendor?.country?.name}</p>
            )}
          </p>
        ) : (
          <p>
            {templateData?.vendor?.street && (
              <p>{templateData?.vendor?.street},</p>
            )}
            {templateData?.vendor?.suburb?.name && (
              <p>
                {templateData?.vendor?.suburb?.name}
                {`${
                  templateData?.vendor?.state?.short_name
                    ? `, ${templateData?.vendor?.state?.short_name}`
                    : ""
                }`}
                {`${
                  templateData?.vendor?.postal_code
                    ? `, ${templateData?.vendor?.postal_code}`
                    : ""
                }`}
              </p>
            )}
          </p>
        )}
        <p className="mt-2 mb-2">
          Dear{" "}
          {/* {templateData?.vendor &&
            (templateData?.vendor?.first_name
              ? templateData?.vendor?.first_name +
                " " +
                templateData?.vendor?.last_name
              : templateData?.vendor?.last_name)} */}
          {formatSubcontractorName(templateData?.vendor)}
        </p>
        {/* Subject */}
        <p className="font-semibold underline mb-2">Re: Remittance Advice</p>

        {/* Body */}
        <div className="mb-4">
          <HtmlContent
            html={
              templateData?.trust?.payment_advice_template
                ? templateData?.trust?.payment_advice_template
                : value
                ? value
                : defaultText
            }
            className="text-justify"
          />
        </div>

        {/* Transaction Table */}
        <table className="w-full text-left mb-4">
          <tbody>
            <tr>
              <td className="py-1 font-medium w-1/2">
                Bank Account Name deposited into:
              </td>
              <td className="py-1">
                {templateData?.vendor?.account_name
                  ? templateData?.vendor?.account_name
                  : "-"}
              </td>
            </tr>
            <tr>
              <td className="py-1 font-medium">BSB:</td>
              <td className="py-1">
                {templateData?.vendor?.bsb ? templateData?.vendor?.bsb : "-"}
              </td>
            </tr>
            <tr>
              <td className="py-1 font-medium">Account Number:</td>
              <td className="py-1">
                {templateData?.vendor?.account_number
                  ? templateData?.vendor?.account_number
                  : "-"}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Amounts Table */}
        <table className="w-1/2 text-left mb-4">
          <tbody>
            <tr>
              <td className="py-1 w-2/3">Gross Amount</td>
              <td className="py-1 text-right">
                {templateData?.total_amount
                  ? `$${formatNumber(Number(templateData?.total_amount))}`
                  : "$0.00"}
              </td>
            </tr>
            <tr>
              <td className="py-1">Professional Fee</td>
              <td className="py-1 text-right">
                {templateData?.fees
                  ? `$${formatNumber(Number(templateData?.fees))}`
                  : "$0.00"}
              </td>
            </tr>
            <tr className="border-t border-black font-semibold">
              <td className="py-1">Balance of this Payment</td>
              <td className="py-1 text-right">
                {templateData?.amount
                  ? `$${formatNumber(Number(templateData?.amount))}`
                  : "$0.00"}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Footer */}
        <p className="mb-4">
          The transaction was processed from our Trust Account:{" "}
          {templateData?.trust?.account_name}, BSB: {templateData?.trust?.bsb}, Account Number:{" "}
          {templateData?.trust?.account_number}. Should you have any queries, please do
          not hesitate to contact our office.
        </p>

        <p className="mb-2">Yours sincerely,</p>
        <p className="font-medium">
          {" "}
          {trustData &&
            (trustData?.business_name
              ? trustData?.business_name
              : trustData?.first_name
              ? trustData?.first_name + " " + trustData?.last_name
              : trustData?.last_name)}
        </p>
        {/* {trustData?.business_name && <p>{trustData?.business_name}</p>} */}
      </div>
    );
  };

  return (
    <ResponsiveDrawerForm
      title="Remittance Advice"
      open={showView}
      setOpen={setShowView}
      read
      popupType={popupType}
      className="md:w-[50vw]"
    >
      <>
        <div className="max-h-[80vh]">
          {/* 👇 Print & Preview Content (SAME DIV) */}
          <div className="rounded-md border overflow-hidden max-h-full overflow-y-auto">
            <div
              ref={printRef}
              className="bg-white text-black p-0 max-w-4xl mx-auto text-sm m-0"
            >
              {htmlData()}
            </div>
          </div>
        </div>
        {/* 👇 Print Button */}
        <div className="flex justify-end">
          <Button onClick={handlePrint}>Print</Button>
        </div>
      </>
    </ResponsiveDrawerForm>
  );
};

export default PaymentAdviceTemplate;
